import {useEffect, useState} from "react";
import {
    Box, Checkbox, CircularProgress, FormControlLabel, Radio, RadioGroup, Typography
} from "@mui/material";
import {getTrainers} from "../../services/TrainerService";
import {colors, borders} from "../ui/colors";

export type ManagedTrainersPickerProps = {
    /** Load the trainer list only while the containing dialog is open. */
    active: boolean;
    managesAll: boolean;
    onManagesAllChange: (next: boolean) => void;
    selectedTrainerIds: number[];
    onSelectedTrainerIdsChange: (next: number[]) => void;
};

type MiniTrainer = {id?: number; name?: string};

/**
 * Which trainers this manager may act on: every trainer in the company, or an explicit list.
 *
 * <p>"All trainers" is not the same as company-admin scope — it follows the company as trainers are
 * added, but the manager still holds only their granted capabilities. The explicit list is exact: an
 * empty selection means no trainers, never all of them, matching the server's fail-closed rule.
 */
const ManagedTrainersPicker = ({
    active, managesAll, onManagesAllChange, selectedTrainerIds, onSelectedTrainerIdsChange,
}: ManagedTrainersPickerProps) => {

    const [trainers, setTrainers] = useState<MiniTrainer[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!active) return;
        setLoading(true);
        getTrainers()
            .then((res: any) => setTrainers(res?.data?.content || res?.data || []))
            .catch(() => setTrainers([]))
            .finally(() => setLoading(false));
    }, [active]);

    const toggle = (trainerId: number) => {
        onSelectedTrainerIdsChange(selectedTrainerIds.includes(trainerId)
            ? selectedTrainerIds.filter(id => id !== trainerId)
            : [...selectedTrainerIds, trainerId]);
    };

    return (
        <Box>
            <Typography sx={{fontSize: "13px", fontWeight: 500, color: colors.textSecondary, marginBottom: "6px"}}>
                מאמנים באחריותו
            </Typography>

            <RadioGroup
                value={managesAll ? "all" : "some"}
                onChange={e => onManagesAllChange(e.target.value === "all")}
            >
                <FormControlLabel
                    value="all"
                    control={<Radio/>}
                    label={<Typography sx={{fontSize: "14px"}}>כל המאמנים בחברה</Typography>}
                />
                <FormControlLabel
                    value="some"
                    control={<Radio/>}
                    label={<Typography sx={{fontSize: "14px"}}>מאמנים נבחרים בלבד</Typography>}
                />
            </RadioGroup>

            {managesAll && (
                <Typography sx={{fontSize: "12px", color: colors.textSecondary, marginTop: "4px"}}>
                    כולל מאמנים שיתווספו לחברה בעתיד.
                </Typography>
            )}

            {!managesAll && (
                <Box sx={{
                    marginTop: "8px", maxHeight: "180px", overflowY: "auto",
                    border: borders.default, borderRadius: borders.radius.md, padding: "8px",
                }}>
                    {loading && <CircularProgress size={20}/>}
                    {!loading && trainers.length === 0 && (
                        <Typography sx={{fontSize: "13px", color: colors.textSecondary}}>
                            אין מאמנים בחברה
                        </Typography>
                    )}
                    {!loading && trainers.map(trainer => (
                        <Box key={trainer.id} sx={{display: "block"}}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={selectedTrainerIds.includes(trainer.id as number)}
                                        onChange={() => toggle(trainer.id as number)}
                                        inputProps={{"aria-label": trainer.name}}
                                    />
                                }
                                label={<Typography sx={{fontSize: "14px"}}>{trainer.name}</Typography>}
                            />
                        </Box>
                    ))}
                </Box>
            )}

            {!managesAll && selectedTrainerIds.length === 0 && !loading && (
                <Typography sx={{fontSize: "12px", color: colors.textSecondary, marginTop: "6px"}}>
                    לא נבחרו מאמנים — המנהל לא יוכל לנהל אף מאמן.
                </Typography>
            )}
        </Box>
    );
};

export default ManagedTrainersPicker;
